import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SiteBackground } from "../components/SiteBackground";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh, refreshUsage } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const tries = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("timeout"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refresh();
          await refreshUsage();
          setState("paid");
          setTimeout(() => nav("/home"), 1500);
          return;
        }
      } catch {}
      if (tries.current >= 12) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, refresh, refreshUsage, nav]);

  return (
    <div className="min-h-screen relative flex items-center justify-center">
      <SiteBackground variant="brand" />
      <div className="relative z-10 pm-card p-8 text-center max-w-md mx-4" data-testid="payment-success">
        {state === "checking" && (<>
          <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin" />
          <h2 className="font-head font-bold text-xl">Confirming your payment…</h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>One moment while we activate your access.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--earn)" }} />
          <h2 className="font-head font-bold text-xl">You're in!</h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Payment complete. Taking you to your dashboard…</p>
        </>)}
        {state === "timeout" && (<>
          <Loader2 className="w-10 h-10 mx-auto mb-4" />
          <h2 className="font-head font-bold text-xl">Still processing</h2>
          <p className="text-sm mt-1 mb-4" style={{ color: "var(--text-2)" }}>This can take a moment. You can head to your dashboard — access unlocks automatically once payment clears.</p>
          <Link to="/home" className="pm-btn inline-block" data-testid="success-home">Go to dashboard</Link>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen relative flex items-center justify-center">
      <SiteBackground variant="brand" />
      <div className="relative z-10 pm-card p-8 text-center max-w-md mx-4" data-testid="payment-cancel">
        <XCircle className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--warning)" }} />
        <h2 className="font-head font-bold text-xl">Checkout canceled</h2>
        <p className="text-sm mt-1 mb-4" style={{ color: "var(--text-2)" }}>No charge was made. You can pick a plan whenever you're ready.</p>
        <Link to="/paywall" className="pm-btn inline-block" data-testid="cancel-back">Back to plans</Link>
      </div>
    </div>
  );
}
